import { isIncome, isPayment, participantName } from "./expense";
import { dayKey } from "./expense-list";
import { toCents } from "./money";
import { spentAtOf } from "./report-time";

export const CSV_MIME_TYPE = "text/csv";

const BOM = "﻿";
const SEPARATOR = ";";
const LINE_END = "\r\n";

export const CSV_HEADER = [
  "Datum",
  "Uhrzeit",
  "Typ",
  "Titel",
  "Bezahlt von",
  "Betrag",
  "Für",
  "Tags",
];

const TIME_ZONE = "Europe/Berlin";

const dateFormat = new Intl.DateTimeFormat("de-DE", {
  timeZone: TIME_ZONE,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const timeFormat = new Intl.DateTimeFormat("de-DE", {
  timeZone: TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

// Spreadsheets run cells starting like a formula, even from a CSV.
const FORMULA_START = /^[=+\-@\t\r]/;

const textCell = (value) => {
  const text = `${value ?? ""}`;
  return FORMULA_START.test(text) ? `'${text}` : text;
};

const quote = (cell) =>
  /[";\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;

export const formatCsvAmount = (cents) => {
  const value = toCents(cents);
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  return `${sign}${Math.floor(abs / 100)},${`${abs % 100}`.padStart(2, "0")}`;
};

const tagNames = (expense) =>
  (Array.isArray(expense?.tags) ? expense.tags : [])
    .map((tag) => `${(typeof tag === "string" ? tag : tag?.name) ?? ""}`.trim())
    .filter(Boolean);

const receiversOf = (expense, participants) => {
  const receivers = Array.isArray(expense.receivers) ? expense.receivers : [];
  const withShare = receivers.filter(
    (r) => toCents(r.absolute_share_in_decimals) > 0,
  );
  const position = (id) => {
    const index = participants.findIndex((p) => `${p.id}` === `${id}`);
    return index < 0 ? participants.length : index;
  };
  return [...(withShare.length ? withShare : receivers)].sort(
    (a, b) => position(a.participant_id) - position(b.participant_id),
  );
};

const describeShares = (expense, participants) =>
  receiversOf(expense, participants)
    .map(
      (r) =>
        `${participantName(participants, r.participant_id)} (${formatCsvAmount(
          r.absolute_share_in_decimals,
        )})`,
    )
    .join(", ");

const byTimeThenId = (a, b) =>
  (a.time ?? Infinity) - (b.time ?? Infinity) ||
  `${a.expense.id}`.localeCompare(`${b.expense.id}`, "en", { numeric: true });

const toRow = ({ expense, time }, participants) => {
  const payment = isPayment(expense);
  return [
    time === null ? "" : dateFormat.format(time),
    time === null ? "" : timeFormat.format(time),
    payment ? "Zahlung" : isIncome(expense) ? "Einnahme" : "Ausgabe",
    textCell(expense.name || (payment ? "Zahlung" : "")),
    textCell(participantName(participants, expense.payer)),
    formatCsvAmount(expense.amount_in_decimals),
    textCell(describeShares(expense, participants)),
    textCell(tagNames(expense).join(", ")),
  ];
};

export const buildExpensesCsv = (group) => {
  const participants = Array.isArray(group?.participants)
    ? group.participants
    : [];
  const expenses = Array.isArray(group?.expenses) ? group.expenses : [];
  const rows = expenses
    .map((expense) => ({ expense, time: spentAtOf(expense) }))
    .sort(byTimeThenId)
    .map((entry) => toRow(entry, participants));
  return (
    BOM +
    [CSV_HEADER, ...rows]
      .map((cells) => cells.map(quote).join(SEPARATOR))
      .join(LINE_END) +
    LINE_END
  );
};

const FILE_NAME_FORBIDDEN = /[\\/:*?"<>|]+/g;

export const csvFileName = (groupName, now = new Date()) => {
  const name =
    `${groupName ?? ""}`
      .replace(FILE_NAME_FORBIDDEN, " ")
      .trim()
      .replace(/\s+/g, "-") || "Gruppe";
  return `Split-It-${name}-${dayKey(now)}.csv`;
};
